import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Button, IconButton } from '@zep-us/zus';
import { XIcon } from '@zep-us/zus-icons';
import { AlertCircle, LoaderCircle } from 'lucide-react';
import { alertDialog } from '@/ui_v3';
import { cn } from '@/utils/tailwind.util';
import { AI_BUILDER_PDF_MAX_SELECTED_PAGES, AI_BUILDER_PDF_MAX_TOTAL_PAGES } from '../constants';
import { useAiBuilderChatCopy } from '../hooks/useAiBuilderChatCopy';
import { usePdfPageDetailPreview } from '../hooks/usePdfPageDetailPreview';
import { pageNumbersToRanges, type PdfPageRange } from '../utils/pdfPageRanges';
import { openPdfPreview, type PdfPreviewDocument } from '../utils/pdfPreviewAdapter';
import { createPdfThumbnailQueue, type PdfThumbnailQueue } from '../utils/pdfThumbnailQueue';
import { AiBuilderPdfPageCard } from './AiBuilderPdfPageCard';
const THUMBNAIL_CONCURRENCY = 3;
const LOADING_SURFACE_DELAY_MS = 200;
type AiBuilderPdfPageSelectionModalProps = {
  file: File;
  open: boolean;
  onCancel: () => void;
  onChooseAnotherFile: () => void;
  onSave: (pageRanges: PdfPageRange[], pageCount: number) => void;
  onTerminalFailure: (failure: PdfPreviewTerminalFailure) => void;
  openPreview?: typeof openPdfPreview;
};
export type PdfPreviewTerminalFailure = 'password' | 'invalid' | 'thumbnail';
type PickerState = {
  status: 'loading';
} | {
  status: 'ready';
  preview: PdfPreviewDocument;
  queue: PdfThumbnailQueue;
} | {
  status: 'page-limit';
};
export const AiBuilderPdfPageSelectionModal = ({
  file,
  onCancel,
  onChooseAnotherFile,
  onSave,
  onTerminalFailure,
  open,
  openPreview = openPdfPreview
}: AiBuilderPdfPageSelectionModalProps) => {
  const copy = useAiBuilderChatCopy().pdfPageSelection;
  const [pickerState, setPickerState] = useState<PickerState>({
    status: 'loading'
  });
  const [selectedPages, setSelectedPages] = useState<Set<number>>(new Set());
  const [surfaceVisible, setSurfaceVisible] = useState(false);
  const terminalFailureHandledRef = useRef(false);
  const readyPreview = pickerState.status === 'ready' ? pickerState.preview : null;
  const {
    loadingPage: detailLoadingPage,
    openPageDetail
  } = usePdfPageDetailPreview(readyPreview);
  useEffect(() => {
    if (open) terminalFailureHandledRef.current = false;
  }, [file, open]);
  const handleTerminalFailure = useCallback((failure: PdfPreviewTerminalFailure) => {
    if (terminalFailureHandledRef.current) return;
    terminalFailureHandledRef.current = true;
    onTerminalFailure(failure);
  }, [onTerminalFailure]);
  useEffect(() => {
    if (!open) {
      setPickerState({
        status: 'loading'
      });
      setSelectedPages(new Set());
      setSurfaceVisible(false);
      return;
    }
    const controller = new AbortController();
    let disposed = false;
    const loadingSurfaceTimer = window.setTimeout(() => {
      if (!disposed) setSurfaceVisible(true);
    }, LOADING_SURFACE_DELAY_MS);
    setPickerState({
      status: 'loading'
    });
    setSelectedPages(new Set());
    setSurfaceVisible(false);
    void openPreview(file, {
      signal: controller.signal
    }).then(preview => {
      if (disposed) {
        void preview.destroy();
        return;
      }
      window.clearTimeout(loadingSurfaceTimer);
      setSurfaceVisible(true);
      if (preview.pageCount > AI_BUILDER_PDF_MAX_TOTAL_PAGES) {
        void preview.destroy();
        setPickerState({
          status: 'page-limit'
        });
        return;
      }
      const defaultPages = Array.from({
        length: preview.pageCount <= AI_BUILDER_PDF_MAX_SELECTED_PAGES ? preview.pageCount : 0
      }, (_, index) => index + 1);
      setSelectedPages(new Set(defaultPages));
      setPickerState({
        status: 'ready',
        preview,
        queue: createPdfThumbnailQueue(THUMBNAIL_CONCURRENCY)
      });
    }).catch(error => {
      if (disposed) return;
      window.clearTimeout(loadingSurfaceTimer);
      setSurfaceVisible(false);
      const code = typeof error === 'object' && error !== null && 'code' in error ? error.code : 'invalid';
      if (code === 'cancelled') return;
      handleTerminalFailure(code === 'password' ? 'password' : 'invalid');
    });
    return () => {
      disposed = true;
      window.clearTimeout(loadingSurfaceTimer);
      controller.abort();
    };
  }, [file, handleTerminalFailure, open, openPreview]);
  useEffect(() => () => {
    if (pickerState.status === 'ready') {
      pickerState.queue.cancel();
      void pickerState.preview.destroy();
    }
  }, [pickerState]);
  const togglePage = (pageNumber: number) => {
    if (!selectedPages.has(pageNumber) && selectedPages.size >= AI_BUILDER_PDF_MAX_SELECTED_PAGES) {
      alertDialog.open({
        header: {
          title: copy.limit
        }
      });
      return;
    }
    setSelectedPages(current => {
      const next = new Set(current);
      if (next.has(pageNumber)) {
        next.delete(pageNumber);
        return next;
      }
      if (next.size >= AI_BUILDER_PDF_MAX_SELECTED_PAGES) {
        return current;
      }
      next.add(pageNumber);
      return next;
    });
  };
  const clearSelection = () => setSelectedPages(new Set());
  const pageNumbers = useMemo(() => pickerState.status === 'ready' ? Array.from({
    length: pickerState.preview.pageCount
  }, (_, index) => index + 1) : [], [pickerState]);
  return <Dialog.Root open={open && surfaceVisible} onOpenChange={nextOpen => !nextOpen && surfaceVisible && onCancel()} data-sentry-element="unknown" data-sentry-component="AiBuilderPdfPageSelectionModal" data-sentry-source-file="AiBuilderPdfPageSelectionModal.tsx">
      <Dialog.Portal data-sentry-element="unknown" data-sentry-source-file="AiBuilderPdfPageSelectionModal.tsx">
        <Dialog.Overlay className="fixed inset-0 z-[390] bg-dimmed" data-sentry-element="unknown" data-sentry-source-file="AiBuilderPdfPageSelectionModal.tsx" />
        <Dialog.Content className="fixed inset-x-0 bottom-0 top-[80px] z-[391] flex min-h-0 flex-col overflow-hidden rounded-t-md bg-default shadow-lg focus:outline-none tablet-sm:inset-auto tablet-sm:left-1/2 tablet-sm:top-1/2 tablet-sm:h-[min(640px,calc(100dvh-48px))] tablet-sm:w-[min(960px,calc(100vw-48px))] tablet-sm:-translate-x-1/2 tablet-sm:-translate-y-1/2 tablet-sm:rounded-md" data-sentry-element="unknown" data-sentry-source-file="AiBuilderPdfPageSelectionModal.tsx">
          <header className="flex h-[60px] shrink-0 items-center gap-sm px-md tablet-sm:h-[68px] tablet-sm:px-lg tablet-sm:pb-xs tablet-sm:pt-lg">
            <Dialog.Title className="min-w-0 flex-1 text-heading-md-bold text-default" data-sentry-element="unknown" data-sentry-source-file="AiBuilderPdfPageSelectionModal.tsx">
              {copy.title}
            </Dialog.Title>
            <Dialog.Close asChild data-sentry-element="unknown" data-sentry-source-file="AiBuilderPdfPageSelectionModal.tsx">
              <IconButton type="button" variant="transparent" intent="neutral" size="sm" aria-label={copy.close} className="shrink-0" data-sentry-element="IconButton" data-sentry-source-file="AiBuilderPdfPageSelectionModal.tsx">
                <XIcon size="md" data-sentry-element="XIcon" data-sentry-source-file="AiBuilderPdfPageSelectionModal.tsx" />
              </IconButton>
            </Dialog.Close>
          </header>

          <Dialog.Description className="sr-only" data-sentry-element="unknown" data-sentry-source-file="AiBuilderPdfPageSelectionModal.tsx">
            {copy.description}
          </Dialog.Description>

          <div className="min-h-0 flex-1 overflow-y-auto bg-alternative px-md py-sm tablet-sm:px-lg">
            {pickerState.status === 'loading' && <div role="status" className="flex h-full min-h-[320px] flex-col items-center justify-center gap-sm text-neutral">
                <LoaderCircle className="size-7 animate-spin" />
                <span>{copy.loading}</span>
              </div>}

            {pickerState.status === 'page-limit' && <div role="alert" className="flex h-full min-h-[320px] flex-col items-center justify-center gap-md text-center">
                <AlertCircle className="size-10 text-destructive" />
                <p className="text-heading-sm-bold">{copy.pageLimitExceeded}</p>
                <div className="flex flex-wrap justify-center gap-xs">
                  <Button type="button" size="md" onClick={onChooseAnotherFile}>
                    {copy.chooseAnother}
                  </Button>
                  <Button type="button" variant="transparent" intent="neutral" size="md" onClick={onCancel}>
                    {copy.cancel}
                  </Button>
                </div>
              </div>}

            {pickerState.status === 'ready' && <div className="grid grid-cols-2 gap-xs tablet-sm:grid-cols-5">
                {pageNumbers.map(pageNumber => <AiBuilderPdfPageCard key={pageNumber} className="h-[210px] w-full tablet-sm:h-[180px]" pageNumber={pageNumber} preview={pickerState.preview} queue={pickerState.queue} selected={selectedPages.has(pageNumber)} detailLoading={detailLoadingPage === pageNumber} onError={() => handleTerminalFailure('thumbnail')} onOpenDetail={imageSrc => void openPageDetail(pageNumber, imageSrc)} onToggle={() => togglePage(pageNumber)} openDetailLabel={copy.openPreview(pageNumber)} openDetailText={copy.openPreviewButton} previewLoadingLabel={copy.previewLoading} toggleSelectionLabel={copy.toggleSelection(pageNumber)} />)}
              </div>}
          </div>

          {pickerState.status === 'ready' && <footer className={cn('relative flex min-h-[80px] shrink-0 items-center border-t border-alternative bg-default px-md pb-[max(16px,env(safe-area-inset-bottom,0px))] pt-md tablet-sm:h-[88px] tablet-sm:rounded-b-md tablet-sm:border-t-0 tablet-sm:px-lg tablet-sm:pb-lg tablet-sm:pt-md tablet-sm:shadow-xl', selectedPages.size === 0 && 'tablet-sm:hidden')}>
              <div className="contents min-w-0 flex-1 items-center gap-sm tablet-sm:flex">
                <IconButton type="button" variant="transparent" intent="neutral" size="md" aria-label={copy.clearSelection} className="hidden tablet-sm:inline-flex" onClick={clearSelection}>
                  <XIcon size="md" />
                </IconButton>
                <div className="contents min-w-0 tablet-sm:block">
                  <p className="hidden text-body-lg-semibold text-neutral tablet-sm:block">
                    {copy.selectedCount(selectedPages.size)}
                  </p>
                </div>
              </div>
              <Button type="button" size="lg" className="w-full tablet-sm:w-[190px]" disabled={selectedPages.size === 0} onClick={() => onSave(pageNumbersToRanges(selectedPages), pickerState.preview.pageCount)}>
                {copy.save}
              </Button>
            </footer>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>;
};